/**
 * Exact fixed-point decimal helpers on BigInt (no floating point for money — ACCOUNTING_RULES §1.6).
 * A value is an integer number of units at a given scale: `toUnits('12.5', 2) === 1250n`.
 * Rounding is always ROUND_HALF_UP (away from zero on a tie), as on the backend (decimal.js).
 */

const DECIMAL = /^([+-])?(\d*)(?:\.(\d*))?$/;

export function pow10(n: number): bigint {
  return 10n ** BigInt(n);
}

/** True for a plain decimal string such as `12`, `0.5`, `-3.25`. */
export function isDecimal(value: string): boolean {
  const m = DECIMAL.exec(value.trim());
  return !!m && (m[2] ?? '') + (m[3] ?? '') !== '';
}

/** Integer division rounded half-up (ties away from zero). */
export function divRound(n: bigint, d: bigint): bigint {
  if (d === 0n) throw new RangeError('Division by zero');
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = (an * 2n + ad) / (ad * 2n);
  return negative ? -q : q;
}

/** Parses a decimal string (or number) into units at `scale`, rounding half-up. Blank or invalid → 0. */
export function toUnits(value: string | number | null | undefined, scale: number): bigint {
  if (value === null || value === undefined) return 0n;
  const text = typeof value === 'number' ? value.toFixed(Math.min(20, scale + 2)) : value.trim();
  const m = DECIMAL.exec(text);
  if (!m) return 0n;
  const sign = m[1] === '-' ? -1n : 1n;
  const int = m[2] || '0';
  const frac = m[3] ?? '';
  const digits = BigInt(int + frac || '0');
  const exact = frac.length;
  const units =
    exact <= scale ? digits * pow10(scale - exact) : divRound(digits, pow10(exact - scale));
  return sign * units;
}

/** Formats units at `scale` as a fixed decimal string: `fromUnits(1250n, 2) === '12.50'`. */
export function fromUnits(units: bigint, scale: number): string {
  const negative = units < 0n;
  const abs = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const int = scale === 0 ? abs : abs.slice(0, -scale);
  const frac = scale === 0 ? '' : `.${abs.slice(-scale)}`;
  return `${negative ? '-' : ''}${int}${frac}`;
}

/** Re-scales units from one scale to another (rounding half-up when reducing). */
export function rescale(units: bigint, from: number, to: number): bigint {
  if (to === from) return units;
  return to > from ? units * pow10(to - from) : divRound(units, pow10(from - to));
}

/** Number of fraction digits written in a decimal string (`'1.250'` → 3). */
export function fractionDigits(value: string): number {
  const m = DECIMAL.exec(value.trim());
  return m?.[3]?.length ?? 0;
}

/** Trims trailing zeros for display or for API inputs: `'12.5000'` → `'12.5'`. */
export function normalize(value: string): string {
  if (!value.includes('.')) return value;
  const out = value.replace(/0+$/, '').replace(/\.$/, '');
  return out === '-0' ? '0' : out;
}

/** a × b for two decimal strings, exactly, at `scale`. */
export function multiply(a: string, b: string, scale: number): string {
  const sa = Math.max(fractionDigits(a), 0);
  const sb = Math.max(fractionDigits(b), 0);
  return fromUnits(rescale(toUnits(a, sa) * toUnits(b, sb), sa + sb, scale), scale);
}

/** a ÷ b for two decimal strings, rounded half-up at `scale`. b = 0 → '0'. */
export function divide(a: string, b: string, scale: number): string {
  const sa = fractionDigits(a);
  const sb = fractionDigits(b);
  const ua = toUnits(a, sa);
  const ub = toUnits(b, sb);
  if (ub === 0n) return fromUnits(0n, scale);
  // a/b = (ua/10^sa)/(ub/10^sb) → units at scale: ua × 10^(sb+scale) / (ub × 10^sa)
  return fromUnits(divRound(ua * pow10(sb + scale), ub * pow10(sa)), scale);
}

/** Compares two decimal strings: -1, 0 or 1. */
export function compare(a: string, b: string): number {
  const s = Math.max(fractionDigits(a), fractionDigits(b));
  const d = toUnits(a, s) - toUnits(b, s);
  return d === 0n ? 0 : d < 0n ? -1 : 1;
}

/** a − b (or a + b) as decimal strings at the larger written scale. */
export function subtract(a: string, b: string): string {
  const s = Math.max(fractionDigits(a), fractionDigits(b));
  return fromUnits(toUnits(a, s) - toUnits(b, s), s);
}

export function add(a: string, b: string): string {
  const s = Math.max(fractionDigits(a), fractionDigits(b));
  return fromUnits(toUnits(a, s) + toUnits(b, s), s);
}
